/**
 * Created by BETALOS on 27/01/2017.
 */
(function () {

    'use strict';

    module.exports = notificationService;

    notificationService.$inject = ["$q", "$http", "mnWebSocket", "$mdDialog", "$translate"];

    const {BehaviorSubject, Subject} = require("rxjs");

    const ALERT_FORM_DIALOG = require('frontdesk/dialogs/alert-form-dialog');
    const NOTIFICATION_DIALOG = require('../dialogs/notification-dialog');

    function notificationService($q, $http, mnWebSocket, $mdDialog, $translate) {
        let self = this;

        // Active alert bucket id — kept for broadcast filtering (handleAlertsChanges)
        let currentActiveAlert = null;
        let alertCount = 0;

        let currentActiveNotification = null;
        let notificationsSubject = new BehaviorSubject([]);

        // Cache per-patient alerts to avoid duplicate WS calls within the same session
        // Invalidated on alerts_updates broadcast (see handleAlertsChanges)
        let patientAlertsCache = {};

        // Session-level set of alert ids the current user has marked as seen.
        // Used to filter them out of subsequent loads, including re-fetches triggered
        // by the alerts_updates broadcast (which otherwise brings back permanent alerts).
        // Reset on page reload — by design, so the user sees all alerts fresh at login.
        let seenInSession = new Set();

        // Emits {patientId: <id> | null} whenever alerts may have changed.
        // patientId === null means "global change" (subscribers should reload regardless).
        // Consumed by <mn-patient-alert> components to refresh in real time.
        let alertsChangedSubject = new Subject();

        // initialize notifications
        self.initialize = initialize;

        self.alertDialog = alertDialog;
        self.removeAlert = removeAlert;

        self.handleAlert = handleAlert;
        self.getPatientAlerts = getPatientAlerts;
        self.getAlertsForPatient = getAlertsForPatient;
        self.invalidatePatientAlertsCache = invalidatePatientAlertsCache;
        self.observeAlertsChanged = () => alertsChangedSubject.asObservable();
        self.getAlertCount = () => alertCount;

        self.markAlertAsSeen = markAlertAsSeen;

        // notification methods
        self.getActiveNotifications = getActiveNotifications;
        self.getNotifications = getNotifications;
        self.notificationDialog = notificationDialog;
        self.handleNotification = handleNotification;
        self.removeNotification = removeNotification;
        self.markNotificationsAsSeen = markNotificationsAsSeen;

        function initialize() {
            return $q.all([
                mnWebSocket.call("shared.notification.Alerts.alerts").then(handleInitialAlertCall),
                mnWebSocket.sub("shared.notification.Alerts.alerts_updates", handleAlertsChanges),

                mnWebSocket.call("shared.notification.Notifications.notifications").then(handleInitialNotificationsCall),
                mnWebSocket.sub("shared.notification.Notifications.notifications_updates", handleNotificationsChanges),
            ]);
        }

        function alertDialog(patient, trigger, alert, ev) {
            return $mdDialog.show(_.assign({}, ALERT_FORM_DIALOG, {
                targetEvent: ev,
                locals: {alert, patient, trigger}
            }));
        }

        function removeAlert(alert, ev) {
            let deferred = $q.defer();

            let confirm = $mdDialog.confirm()
                .multiple(true)
                .targetEvent(ev)
                .ariaLabel('alert_remove_confirm')
                .ok($translate['instant']('confirm_ok'))
                .cancel($translate['instant']('confirm_cancel'))
                .title($translate['instant']('alert_remove_confirm', alert))

            $mdDialog.show(confirm).then(remove, deferred.reject);

            function remove() {
                const url = `/api/alert-form/${alert.id}/`
                $http.delete(url).then(deferred.resolve);
            }

            return deferred.promise;
        }

        function handleAlert(alert, trigger) {
            const deferred = $q.defer();
            const url = `/api/alert-form/${alert.id ? alert.id + '/' : ''}?trigger=${trigger}`;

            $http.post(url, alert)
                .then(success, deferred.reject)

            function success(response) {
                deferred.resolve(response.data);
            }

            return deferred.promise;
        }

        function getPatientAlerts(patient) {
            const deferred = $q.defer();
            const url = `/api/alert-form/?patient=${patient.id}`;

            $http.get(url)
                .then(success, deferred.reject)

            function success(response) {
                deferred.resolve(response.data);
            }

            return deferred.promise;
        }

        function getAlertsForPatient(patientId) {
            // Returns a promise resolving to the alert list for this specific patient.
            // Caches the PROMISE itself (not just the resolved value) so that concurrent
            // calls for the same patient share a single WS round-trip.
            // Filters out alerts marked as seen in this session (permanent or not) so
            // they don't reappear after a broadcast-triggered refresh.
            if (_.isNil(patientId)) {
                return $q.resolve([]);
            }

            if (patientAlertsCache[patientId]) {
                return patientAlertsCache[patientId];
            }

            const promise = mnWebSocket.call("shared.notification.Alerts.alerts_for_patient", {patient: patientId})
                .then(
                    (alerts) => {
                        const filtered = _.filter(alerts || [], a => !seenInSession.has(a.id));
                        // Replace the in-flight promise with a resolved one holding the value
                        patientAlertsCache[patientId] = $q.resolve(filtered);
                        return filtered;
                    },
                    (err) => {
                        // Drop the cached failed promise so a retry can happen
                        delete patientAlertsCache[patientId];
                        return $q.reject(err);
                    }
                );

            patientAlertsCache[patientId] = promise;
            return promise;
        }

        function invalidatePatientAlertsCache(patientId) {
            // Clear cache entry when alerts change for a specific patient, or all entries
            // if no patientId provided (e.g. after a global alerts_updates broadcast).
            // Emits on alertsChangedSubject so mounted <mn-patient-alert> components reload.
            if (_.isNil(patientId)) {
                patientAlertsCache = {};
                alertsChangedSubject.next({patientId: null});
            } else {
                delete patientAlertsCache[patientId];
                alertsChangedSubject.next({patientId: patientId});
            }
        }

        function handleInitialAlertCall(data) {
            // Response shape from backend: {id, count, alerts: []}
            // We keep only {id, count} — per-patient alerts are loaded on demand.
            currentActiveAlert = _.get(data, "id", null);
            alertCount = _.get(data, "count", 0);
        }

        function handleAlertsChanges(data) {
            const updatedItems = data && data['updated_items'];
            const patientId = data && data['patient'];

            if (!currentActiveAlert) return;

            // Backward-compatible match:
            // - new format: array of ActiveAlerts IDs (targeted)
            // - legacy format: boolean true (fire-hose — should not happen anymore)
            const matchesBroadcast = (_.isBoolean(updatedItems) && updatedItems) || _.includes(updatedItems, currentActiveAlert);
            if (!matchesBroadcast) return;

            if (!_.isNil(patientId)) {
                invalidatePatientAlertsCache(patientId);
            } else {
                invalidatePatientAlertsCache();
            }
        }

        function markAlertAsSeen(alert) {
            // Track the alert id locally so the upcoming broadcast-triggered refetch
            // won't bring it back (especially for permanent alerts, which the backend
            // handler still returns from alerts_for_patient).
            seenInSession.add(alert);
            return mnWebSocket.call("shared.notification.Alerts.mark_alert_as_seen", {
                alertItem: alert, currentActiveAlerts: currentActiveAlert
            });
        }

        // notification methods
        function handleInitialNotificationsCall(data) {
            currentActiveNotification = data.id;
            notificationsSubject.next(data.notifications);
        }

        function handleNotificationsChanges(data) {
            if (!currentActiveNotification) return;

            if (_.includes(data['updated_items'], currentActiveNotification)) {
                mnWebSocket.call("shared.notification.Notifications.notifications").then(handleInitialNotificationsCall)
            }
        }

        function getActiveNotifications() {
            return notificationsSubject;
        }

        function getNotifications() {
            const deferred = $q.defer();
            const url = `/api/notification/`;

            $http.get(url)
                .then(success, deferred.reject)

            function success(response) {
                deferred.resolve(response.data);
            }

            return deferred.promise;
        }

        function notificationDialog(notification, patient, ev) {
            $mdDialog.show(_.assign({}, NOTIFICATION_DIALOG, {
                targetEvent: ev,
                locals: _.assign({}, notification ? {notification} : {}, patient ? {patient} : {})
            }));
        }

        function handleNotification(notification) {
            const deferred = $q.defer();
            const url = `/api/notification/${notification.id ? notification.id + '/' : ''}`;

            $http.post(url, notification)
                .then(success, deferred.reject)

            function success(response) {
                deferred.resolve(response.data);
            }

            return deferred.promise;
        }

        function removeNotification(item, ev) {
            let deferred = $q.defer();

            let confirm = $mdDialog.confirm()
                .multiple(true)
                .targetEvent(ev)
                .ariaLabel('notification_remove_confirm')
                .ok($translate['instant']('confirm_ok'))
                .cancel($translate['instant']('confirm_cancel'))
                .title($translate['instant']('notification_remove_confirm', item))

            $mdDialog.show(confirm).then(remove, deferred.reject);

            function remove() {
                const url = `/api/notification/${item.id}/`
                $http.delete(url).then(deferred.resolve);
            }

            return deferred.promise;
        }

        function markNotificationsAsSeen(items) {
            return mnWebSocket.call("shared.notification.Notifications.mark_as_seen", {
                items: _.map(items, 'id'), currentActiveItem: currentActiveNotification
            });
        }

    }

})();
